import { BrowserRouter, Routes, Route, useLocation, useParams, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import * as Sentry from '@sentry/react';
import '@/App.css';

import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { FavoritesProvider } from './contexts/FavoritesContext';
import PushNotificationHandler from './components/PushNotificationHandler';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import MacroSetupChoice from './pages/onboarding/MacroSetupChoice';
import ManualMacros from './pages/onboarding/ManualMacros';
import GoalSelection from './pages/onboarding/GoalSelection';
import MacroQuiz from './pages/onboarding/MacroQuiz';
import Allergens from './pages/onboarding/Allergens';
import Home from './pages/Home';
import PlanningHub from './pages/PlanningHub';
import GuidedPlanning from './pages/GuidedPlanning';
import DailyPlan from './pages/DailyPlan';
import WeeklyPlan from './pages/WeeklyPlan';
import Meals from './pages/Meals';
import MealView from './pages/MealView';
import GroupedMealView from './pages/GroupedMealView';
import { wantsGroupedView, legacyCustomMealTarget } from './pages/mealDetailView';
import Favorites from './pages/Favorites';
import Progress from './pages/Progress';
import Grocery from './pages/Grocery';
import Settings from './pages/Settings';
import LegalAbout from './pages/LegalAbout';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfUse from './pages/TermsOfUse';
import SourcesDisclaimer from './pages/SourcesDisclaimer';
import ImportRecipe from './pages/ImportRecipe';
import AddCustomMeal from './pages/AddCustomMeal';
import LogFood from './pages/LogFood';
import GoalComplete from './pages/GoalComplete';
import Pantry from './pages/Pantry';
import AdminMealList from './pages/admin/AdminMealList';
import AdminMealImport from './pages/admin/AdminMealImport';
import MealEditor from './pages/admin/MealEditor';
import AdminCoreFoods from './pages/admin/AdminCoreFoods';
import CoreFoodEditor from './pages/admin/CoreFoodEditor';
import TagManager from './pages/admin/TagManager';
import UsersAccess from './pages/admin/UsersAccess';
import WeeklyMealPlanner from './pages/WeeklyMealPlanner';
import WeeklyPlanReview from './pages/WeeklyPlanReview';
import PlanReview from './pages/PlanReview';
import AIFoodReviewDemo from './pages/ailog/AIFoodReviewDemo';
import AILogStart from './pages/ailog/AILogStart';
import AILogAnalyzing from './pages/ailog/AILogAnalyzing';
import TasteSetupDemo from './pages/TasteSetupDemo';
import ProtectedRoute from './components/ProtectedRoute';
import PlanningGate from './components/PlanningGate';
import OnboardingRoute from './components/OnboardingRoute';
import RouteErrorBoundary from './components/RouteErrorBoundary';

// Canonical meal-detail dispatcher: ONE user-facing route (/meal-view/:mealId). Grouped/rebuild-backed
// meals render the specialized grouped view; ordinary meals render the standard MealView.
function MealDetail() {
  const location = useLocation();
  return wantsGroupedView(location.search, location.state) ? <GroupedMealView /> : <MealView />;
}

// Legacy deep-link safety: /custom-meal/:mealId -> canonical /meal-view/:mealId (these links were always
// grouped), preserving any existing query params + router state.
function LegacyCustomMealRedirect() {
  const { mealId } = useParams();
  const location = useLocation();
  return <Navigate to={legacyCustomMealTarget(mealId, location.search)} replace state={location.state} />;
}

function AppRouter() {
  const location = useLocation();
  
  // Handle OAuth callback with session_id or magic_token in URL fragment or query params
  if (location.hash?.includes('session_id=') || location.hash?.includes('magic_token=') ||
      location.search?.includes('magic_token=') || location.search?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<Login />} />
      <Route path="/login" element={<Login />} />
      <Route path="/auth-callback" element={<AuthCallback />} />
      <Route path="/legal" element={<LegalAbout />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfUse />} />
      <Route path="/sources" element={<SourcesDisclaimer />} />
      <Route path="/taste-setup-demo" element={<RouteErrorBoundary><TasteSetupDemo /></RouteErrorBoundary>} />
      <Route path="/ai-log-review-demo" element={<RouteErrorBoundary><AIFoodReviewDemo /></RouteErrorBoundary>} />
      <Route path="/ai-log" element={<RouteErrorBoundary><AILogStart /></RouteErrorBoundary>} />
      <Route path="/ai-log/preview" element={<Navigate to="/ai-log" replace />} />
      <Route path="/ai-log/analyzing" element={<RouteErrorBoundary><AILogAnalyzing /></RouteErrorBoundary>} />
      <Route path="/ai-log/review" element={<RouteErrorBoundary><AIFoodReviewDemo /></RouteErrorBoundary>} />
      
      {/* Onboarding routes - REQUIRES authentication, runs ONCE per user */}
      <Route path="/onboarding/setup" element={<OnboardingRoute><MacroSetupChoice /></OnboardingRoute>} />
      <Route path="/onboarding/manual-macros" element={<OnboardingRoute><ManualMacros /></OnboardingRoute>} />
      <Route path="/onboarding/goal" element={<OnboardingRoute><GoalSelection /></OnboardingRoute>} />
      <Route path="/onboarding/quiz" element={<OnboardingRoute><MacroQuiz /></OnboardingRoute>} />
      <Route path="/onboarding/allergens" element={<OnboardingRoute><Allergens /></OnboardingRoute>} />
      
      {/* Protected routes - REQUIRES authentication AND completed onboarding.
          Top-level user-facing tabs are additionally wrapped in
          RouteErrorBoundary so a render-time exception is contained to
          that tab (user can still switch tabs via BottomNav) instead of
          tripping the app-level Sentry fallback. */}
      <Route path="/home" element={<ProtectedRoute><RouteErrorBoundary><Home /></RouteErrorBoundary></ProtectedRoute>} />
      <Route path="/plan" element={<ProtectedRoute><PlanningGate><RouteErrorBoundary><PlanningHub /></RouteErrorBoundary></PlanningGate></ProtectedRoute>} />
      <Route path="/plan/guided" element={<ProtectedRoute><PlanningGate><RouteErrorBoundary><GuidedPlanning /></RouteErrorBoundary></PlanningGate></ProtectedRoute>} />
      <Route path="/plan/daily" element={<ProtectedRoute><PlanningGate><RouteErrorBoundary><DailyPlan /></RouteErrorBoundary></PlanningGate></ProtectedRoute>} />
      <Route path="/plan/weekly" element={<ProtectedRoute><PlanningGate><RouteErrorBoundary><WeeklyPlan /></RouteErrorBoundary></PlanningGate></ProtectedRoute>} />
      <Route path="/plan/weekly-planner" element={<ProtectedRoute><PlanningGate><RouteErrorBoundary><WeeklyMealPlanner /></RouteErrorBoundary></PlanningGate></ProtectedRoute>} />
      <Route path="/plan/weekly-review" element={<ProtectedRoute><PlanningGate><RouteErrorBoundary><WeeklyPlanReview /></RouteErrorBoundary></PlanningGate></ProtectedRoute>} />
          <Route path="/plan/review" element={<ProtectedRoute><PlanningGate><RouteErrorBoundary><PlanReview /></RouteErrorBoundary></PlanningGate></ProtectedRoute>} />
      <Route path="/meals" element={<ProtectedRoute><RouteErrorBoundary><Meals /></RouteErrorBoundary></ProtectedRoute>} />
      <Route path="/meal-view/:mealId" element={<ProtectedRoute><RouteErrorBoundary><MealDetail /></RouteErrorBoundary></ProtectedRoute>} />
      <Route path="/custom-meal/:mealId" element={<ProtectedRoute><LegacyCustomMealRedirect /></ProtectedRoute>} />
      <Route path="/favorites" element={<ProtectedRoute><RouteErrorBoundary><Favorites /></RouteErrorBoundary></ProtectedRoute>} />
      <Route path="/progress" element={<ProtectedRoute><RouteErrorBoundary><Progress /></RouteErrorBoundary></ProtectedRoute>} />
      <Route path="/grocery" element={<ProtectedRoute><RouteErrorBoundary><Grocery /></RouteErrorBoundary></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><RouteErrorBoundary><Settings /></RouteErrorBoundary></ProtectedRoute>} />
      <Route path="/import-recipe" element={<ProtectedRoute><RouteErrorBoundary><ImportRecipe /></RouteErrorBoundary></ProtectedRoute>} />
      <Route path="/add-meal" element={<ProtectedRoute><RouteErrorBoundary><AddCustomMeal /></RouteErrorBoundary></ProtectedRoute>} />
      <Route path="/log-food" element={<ProtectedRoute><RouteErrorBoundary><LogFood /></RouteErrorBoundary></ProtectedRoute>} />
      <Route path="/goal-complete" element={<ProtectedRoute><RouteErrorBoundary><GoalComplete /></RouteErrorBoundary></ProtectedRoute>} />
      <Route path="/pantry" element={<ProtectedRoute><RouteErrorBoundary><Pantry /></RouteErrorBoundary></ProtectedRoute>} />
      
      {/* Admin Routes */}
      <Route path="/admin/meals" element={<ProtectedRoute><AdminMealList /></ProtectedRoute>} />
      <Route path="/admin/meals/import" element={<ProtectedRoute><AdminMealImport /></ProtectedRoute>} />
      <Route path="/admin/meals/new" element={<ProtectedRoute><MealEditor /></ProtectedRoute>} />
      <Route path="/admin/meals/:mealId/edit" element={<ProtectedRoute><MealEditor /></ProtectedRoute>} />
      <Route path="/admin/core-foods" element={<ProtectedRoute><AdminCoreFoods /></ProtectedRoute>} />
      <Route path="/admin/core-foods/new" element={<ProtectedRoute><CoreFoodEditor /></ProtectedRoute>} />
      <Route path="/admin/core-foods/:foodId/edit" element={<ProtectedRoute><CoreFoodEditor /></ProtectedRoute>} />
      <Route path="/admin/tags" element={<ProtectedRoute><TagManager /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute><UsersAccess /></ProtectedRoute>} />
      
      {/* Catch all - redirect to login */}
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

// Sentry Error Fallback Component
function SentryFallback({ error }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="text-center max-w-md">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Something went wrong</h2>
        <p className="text-gray-600 mb-4">We've been notified and are working to fix it.</p>
        <button 
          onClick={() => window.location.href = "/"}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Return to Home
        </button>
      </div>
    </div>
  );
}

function App() {
  return (
    <Sentry.ErrorBoundary fallback={SentryFallback}>
      <div className="App">
        <BrowserRouter>
          <SubscriptionProvider>
            <FavoritesProvider>
              <PushNotificationHandler />
              <AppRouter />
              <Toaster position="top-center" richColors />
            </FavoritesProvider>
          </SubscriptionProvider>
        </BrowserRouter>
      </div>
    </Sentry.ErrorBoundary>
  );
}

export default App;
